import withParams from "@src/components/widgets/HOC/withParams";
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetHeaderWithHighlightProductPetitPrinceParams } from "../interface";
import type { ICompositionVariant } from "design-system/src/Components/WidgetPart/LandingHighlighProduct/LandingHighlighProduct";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";

import useCompositionBox from "@src/components/widgets/hooks/useCompositionBox";
import { useTranslation } from "@src/hooks/useTranslation";
import LandingHeaderHighlightProduct from "design-system/src/Components/WidgetPart/LandingHeaderHighlightProduct/LandingHeaderHighlightProduct";
import StyledDiv from "design-system/src/Components/StyledDiv/StyledDiv";
import useBreakpoint from "design-system/src/Components/Grid/hooks/useBreakpoint";

const bottomGap = 300;
const mobileBottomGap = 78;
const mobileAspectRatio = 413 / 435;
const HeaderWithHighlightProductPetitPrince: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetHeaderWithHighlightProductPetitPrinceParams>(
    (props) => {
      const { p } = props;
      const stickyRef = useRef<HTMLDivElement>(null);
      const overlayRef = useRef<HTMLDivElement>(null);
      const rafRef = useRef<number | null>(null);
      const [stickyTop, setStickyTop] = useState(0);
      const { mobile } = useBreakpoint();

      // Calculate sticky top position
      useEffect(() => {
        const calculateStickyTop = () => {
          if (stickyRef.current) {
            const elementHeight = stickyRef.current.offsetHeight;
            const viewportHeight = window.innerHeight;
            const topValue =
              elementHeight - viewportHeight - (mobile ? 0 : bottomGap - 100);
            setStickyTop(topValue > 0 ? -topValue : 0);
          }
        };

        calculateStickyTop();
        window.addEventListener("resize", calculateStickyTop);

        return () => {
          window.removeEventListener("resize", calculateStickyTop);
        };
      }, [mobile]);

      // Calculate overlay opacity when next section enters viewport from bottom
      const updateOverlayOpacity = useCallback(() => {
        if (!overlayRef.current || !stickyRef.current) {
          return;
        }

        // Get scroll position
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;

        // Get element dimensions and position
        const elementOffsetTop = stickyRef.current.offsetTop;
        const elementHeight = stickyRef.current.offsetHeight;
        const viewportHeight = window.innerHeight;

        // Calculate when the next section starts to appear at bottom of viewport
        // This happens when: scrollTop = (elementOffsetTop + elementHeight) - viewportHeight
        const nextSectionStartsAt =
          elementOffsetTop + elementHeight - viewportHeight;

        // Calculate how much we've scrolled past that trigger point
        const scrolledPastTrigger = Math.max(
          0,
          scrollTop - nextSectionStartsAt
        );

        // Define the range over which opacity increases (e.g., one viewport height)
        const opacityRange = viewportHeight;

        // Calculate opacity progress (0 to 1)
        const coverProgress = Math.min(1, scrolledPastTrigger / opacityRange);

        overlayRef.current.style.setProperty(
          "--overlay-opacity",
          String(coverProgress * 0.9)
        );
      }, []);

      const handleScroll = useCallback(() => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = requestAnimationFrame(updateOverlayOpacity);
      }, [updateOverlayOpacity]);

      useEffect(() => {
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
          window.removeEventListener("scroll", handleScroll);
          if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
          }
        };
      }, [handleScroll]);

      // Initial overlay check
      useEffect(() => {
        updateOverlayOpacity();
      }, [updateOverlayOpacity, stickyTop]);

      // Highlight Product params
      const highlightCtaAction = p("highlight_cta_action");
      const isAddToCart: boolean = highlightCtaAction === "add_to_cart";
      const id_product = p("highlight_product") || p("id_product");
      const ctaLabel = p("highlight_cta_label") || "";
      const mediaSource = p("media_source") || "image";
      const { t } = useTranslation("Product");
      const { generateClick: generateHighlightClick, getSubmitLabel } =
        useCtaAction(id_product || "");
      const { prepareCompositions, prepareProduct } = useCompositionBox();

      const productModel = prepareProduct(p("highlight_product") || "");

      const highlightHandleClick = generateHighlightClick(
        highlightCtaAction && highlightCtaAction === "open_link"
          ? {
              type: highlightCtaAction,
              link: p("highlight_link") || "#",
            }
          : {
              type: highlightCtaAction || "product_card",
            }
      );

      const compositions: ICompositionVariant | undefined = useMemo(() => {
        const productIds: string[] = (p("box_products") || "")
          .split(",")
          .map((id) => id.trim());
        const productQuantity: number[] = (p("box_products_itemnbr") || "")
          .split(",")
          .map((i) => +i);
        const newProductIds: string[] = (p("box_products_new") || "")
          .split(",")
          .map((id) => id.trim());
        return {
          type: "box",
          list:
            prepareCompositions(productIds, productQuantity, newProductIds) ||
            [],
        } as ICompositionVariant;
      }, [p, prepareCompositions]);

      const displayFromMention =
        p("product_display_from_mention") === "1"
          ? t("À partir de", "Product")
          : undefined;

      const vaePrice = productModel?.getPriceVae();
      const vadPrice = productModel?.getPriceVad();

      return (
        <>
          <style>
            {`
              .layout-main .widget-instance:not([data-template="header-with-highlight-product-petit-prince"]) {
                position: relative;
                z-index: 2;
                background: white;
              }
              .layout-footer {
                position: relative;
                z-index: 1;
              }
              .layout-main .widget-instance[data-template="header-with-highlight-product-petit-prince"] {
                overflow: visible;
                position: sticky;
                top: ${stickyTop}px;
              }
            `}
          </style>
          <StyledDiv ref={stickyRef} style={{ position: "relative" }}>
            {/* Overlay */}
            <div
              ref={overlayRef}
              className="overlay"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "#000",
                opacity: "var(--overlay-opacity, 0)",
                pointerEvents: "none",
                zIndex: 3,
                transform: "translate3d(0, 0, 0)",
                transition: "opacity 0.1s ease-out",
              }}
              aria-hidden="true"
            />
            <LandingHeaderHighlightProduct
              styles={{
                paddingBottom: bottomGap,
                overflow: "hidden",
                mobile: {
                  paddingBottom: mobileBottomGap,
                },
              }}
              // Header props
              headerProps={{
                description: p("description"),
                ctaLabel: isAddToCart
                  ? getSubmitLabel(false, ctaLabel)
                  : ctaLabel,
              }}
              // Product props
              productProps={{
                variant: "composition-title",
                title: p("highlight_title") || "",
                subtitle: p("highlight_subtitle"),
                ctaLabel: isAddToCart
                  ? getSubmitLabel(false, ctaLabel)
                  : ctaLabel,
                description: p("highlight_description"),
                image: p("highlight_product_image"),
                video: mediaSource === "video" ? p("video_src") : undefined,

                onClick: highlightHandleClick,
                backgroundImage: {
                  desktop: p("background_media_desktop"),
                  mobile: p("background_media_mobile"),
                },
                compositions: compositions,
                price: productModel?.formatPrice(),
                priceSubtitle:
                  productModel && productModel.getQuantity() > 1
                    ? productModel.displayPieces()
                    : undefined,
                product_display_from_mention: displayFromMention,
                product_price_vae: vaePrice,
                product_price_vad: vadPrice,
                vaeLabel: t("À emporter", "Product"),
                vadLabel: t("En livraison", "Product"),
                compositionLabel: p("composition") || "",
                mobileAspectRatio: mobileAspectRatio,
              }}
            />
          </StyledDiv>
        </>
      );
    }
  );

export default HeaderWithHighlightProductPetitPrince;
